import {Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {Subject} from 'rxjs';
import {take, takeUntil} from 'rxjs/operators';
import {Sort} from '@angular/material/sort';
import {MatTableDataSource} from '@angular/material/table';
import {MatDialog} from '@angular/material/dialog';
import {MatPaginator, PageEvent} from '@angular/material/paginator';
import {getSortDirection} from '../../../../../common/util/Util';
import {DateUtil} from '../../../../../common/util/DateUtil';
import {TableControl} from '../../../../../common/models/TableControl';
import {LimitDTO} from '../../../../models/LimitDTO';
import {EntRequestBuilder} from '../../../../services/ent-request-builder';
import {PagedResponseDTO} from '../../../../../common/models/PagedResponseDTO';
import {SharedService} from '../../../../../common/services/shared-service';
import {LimitService} from '../../../../services/limit.service';
import {ConfirmationDialogComponent} from '../../../../../common/dialogs/confirmation-dialog/confirmation-dialog.component';
import {AddLimitDialogComponent} from '../../../dialogs/add-limit-dialog/add-limit-dialog.component';
import {detailsExpand} from '../../../../../common';

@Component({
  selector: 'app-limits',
  templateUrl: './limits.component.html',
  styleUrls: [
    '../../../../../../../styles/global/_management-styles.scss'
  ],
  animations: [detailsExpand]
})
export class LimitsComponent implements OnInit, OnDestroy {
  readonly destroyed$ = new Subject();
  readonly dateFormat = DateUtil.dateFormat;
  readonly dateTimeFormat = DateUtil.dateTimeFormat;

  limitsDataSource: MatTableDataSource<LimitDTO> = new MatTableDataSource();
  limitsDisplayedColumns: string[] = [
    'icons',
    'limitId',
    'name',
    'limitType',
    'measure',
    'accumulateBy',
    'accumulateAt',
    'value',
    'startDate',
    'endDate'
  ];
  limitsExpandedRow: LimitDTO | null;
  limitsTableControl: TableControl;

  // MatPaginator Inputs
  @ViewChild('limitsPaginator', {static: false}) limitsPaginator: MatPaginator;

  constructor(public dialog: MatDialog,
              private limitService: LimitService,
              private route: ActivatedRoute,
              private sharedService: SharedService) {
    const controls = EntRequestBuilder.getTableControls();
    this.limitsTableControl = new TableControl(
      controls.length,
      controls.pageSize,
      controls.pageNumber,
      controls.pageSizeOptions,
      'limitId',
      'DESCENDING',
      'name',
      ''
    );
  }

  ngOnInit(): void {
    this.getListOfLimits(this.limitsTableControl);
    this.initSubscriptions();
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  initSubscriptions() {
    this.route
      .queryParams
      .subscribe(params => {
        if (params.dialog === "true") {
          this.openLimitDialog();
        }
      });
  }

  pageChanged($event: PageEvent) {
    this.limitsTableControl.pageNumber = $event.pageIndex;
    this.limitsTableControl.pageSize = $event.pageSize;
    this.getListOfLimits(this.limitsTableControl);
  }

  sortData($event: Sort) {
    if (!$event.direction) {
      return;
    }
    if (this.limitsPaginator != null) {
      this.limitsPaginator.firstPage();
    }
    this.limitsTableControl.pageNumber = 0;

    if ($event.active === 'limitType') {
      this.limitsTableControl.sortField = 'limitTypeByLimitTypeId';
    }
    else if ($event.active === 'measure') {
      this.limitsTableControl.sortField = 'measureByMeasureId.name';
    }
    else if ($event.active === 'accumulateBy') {
      this.limitsTableControl.sortField = 'accumulateByByAccumulateById.name';
    }
    else if ($event.active === 'accumulateAt') {
      this.limitsTableControl.sortField = 'accumulateAtByAccumulateAtId.name';
    }
    else {
      this.limitsTableControl.sortField = $event.active;
    }
    this.limitsTableControl.sortDirection = getSortDirection($event.direction);
    this.getListOfLimits(this.limitsTableControl);
  }

  onValueChanged(value: string) {
    this.limitsTableControl.filterField = 'name';
    this.limitsTableControl.filterValue = value;
    this.getListOfLimits(this.limitsTableControl);
  }

  onDeleteLimitConfirmation(event: MouseEvent, limit: LimitDTO) {
    event.preventDefault();
    event.stopPropagation();

    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '40vw',
      data: `Are you sure you wish to delete this ${limit.name} limit?`
    });

    dialogRef
      .afterClosed()
      .subscribe((isConfirmed) => {
        if (isConfirmed) {
          this.onDeleteLimitConfirmed(limit.limitId);
        }
      });
  }

  onDeleteLimitConfirmed(limitId: bigint) {
    this.limitService.deleteLimit(limitId)
      .pipe(
        take(1)
      )
      .subscribe(
        (response) => {
          this.sharedService.openSnackBar(`Limit with ${limitId} deleted`, 'Dismiss');
          this.getListOfLimits(this.limitsTableControl);
        },
        (errorResponse) => {
          const error = errorResponse.error;
          this.sharedService.openErrorDialog(error.businessMessage);
        }
      );
  }

  getListOfLimits(tableControl: TableControl): void {
    const pagedRequestDTO = EntRequestBuilder.getListOfLimitsPagedRequestDTO(tableControl);
    this.limitService
      .getListOfLimits(pagedRequestDTO)
      .pipe(
        takeUntil(this.destroyed$)
      )
      .subscribe(
        (response: PagedResponseDTO<LimitDTO>) => {
          this.limitsDataSource.data = response.resultList;
          this.limitsTableControl.length = response.totalRecords;
        },
        (errorResponse) => {
          const error = errorResponse.error;
          this.sharedService.openErrorDialog(error.businessMessage);
        }
      );
  }

  editEntry(event: MouseEvent, limitId: bigint) {
    event.preventDefault();
    event.stopPropagation();
    this.openLimitDialog(limitId);
  }

  openLimitDialog(limitId?: bigint) {
    const dialogRef = this.dialog.open(AddLimitDialogComponent, {
      width: '35vw',
      data: {
        limitId: limitId
      },
      disableClose: true
    });

    dialogRef.afterClosed().subscribe(
      (result) => {
        if (result.isSaved) {
          this.getListOfLimits(this.limitsTableControl);
        }
      },
      (error) => {
        this.sharedService.openErrorDialog(error.message);
      }
    );
  }

}
